import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleInfo,
  faCircleCheck,
  faTriangleExclamation,
  faCircleExclamation,
  faUser,
  faRightFromBracket,
  faHouse,
  faInbox,
  faBell,
  faLayerGroup,
  faFolder
} from '@fortawesome/free-solid-svg-icons';

import { ICON_DATA } from '../icons/iconData';

const ALIAS: Record<string, string> = {
  search: 'magnifying-glass',
  x: 'xmark',
  chevDown: 'chevron-down',
  chevRight: 'chevron-right',
  more: 'ellipsis-stroke',
  edit: 'pen-line',
  copy: 'clone'
};

const FA_FALLBACK: Record<string, IconDefinition> = {
  info: faCircleInfo,
  success: faCircleCheck,
  warn: faTriangleExclamation,
  danger: faCircleExclamation,
  user: faUser,
  logout: faRightFromBracket,
  home: faHouse,
  inbox: faInbox,
  bell: faBell,
  layers: faLayerGroup,
  folder: faFolder
};

export type IconName = string;
export type IconWeight = 'light' | 'regular';

export interface IconProps {
  name: IconName;
  weight?: IconWeight;
  size?: number | string;
  className?: string;
  title?: string;
  color?: string;
  style?: React.CSSProperties;
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export function Icon({ name, size = 16, className, title, color, style }: IconProps) {
  const dsName = ALIAS[name] ?? name;
  const entry = ICON_DATA[dsName];

  if (entry) {
    const inner = title
      ? `<title>${escapeHtml(title)}</title>${entry.body}`
      : entry.body;
    return (
      <svg
        className={className}
        width={size}
        height={size}
        viewBox={entry.viewBox}
        fill="currentColor"
        focusable={false}
        role={title ? 'img' : undefined}
        aria-hidden={title ? undefined : true}
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          flexShrink: 0,
          color,
          ...style
        }}
        dangerouslySetInnerHTML={{ __html: inner }}
      />
    );
  }

  const faIcon = FA_FALLBACK[name];
  if (!faIcon) return null;
  return (
    <FontAwesomeIcon
      icon={faIcon}
      className={className}
      title={title}
      style={{ width: size, height: size, fontSize: size, color, ...style }}
    />
  );
}
